import { useMutation, useQuery } from '@tanstack/react-query';
import { getUnixTime } from 'date-fns';
import { API_ENDPOINTS } from 'Constants/env';
import { VoicemailBadgeCounter } from 'Interfaces/voicemails';
import { MissedCallsCountResponse, PersonHistoryResponse } from 'Interfaces/calls';
import API from '~/api';

export const QUERY_KEYS = {
  voicemails: ['voicemails/count'],
  personHistory: ['person-history'],
  missedCalls: (timestamp?: number) =>
    timestamp !== undefined ? ['missed-calls', timestamp] : ['missed-calls'],
};

export const useBadgeCounters = () => {
  const voicemailQuery = useQuery<VoicemailBadgeCounter>({
    queryKey: QUERY_KEYS.voicemails,
    queryFn: async () => {
      const response = await API.get<VoicemailBadgeCounter>(
        API_ENDPOINTS.UnreadVoicemailCount
      );
      return response?.data;
    },
    enabled: true,
  });

  const personHistoryQuery = useQuery<PersonHistoryResponse>({
    queryKey: QUERY_KEYS.personHistory,
    queryFn: async () => {
      const response = await API.get<PersonHistoryResponse>(
        API_ENDPOINTS.PersonHistory
      );
      return response?.data;
    },
    enabled: true,
  });

  const missedCallsQuery = useQuery<MissedCallsCountResponse>({
    queryKey: QUERY_KEYS.missedCalls(personHistoryQuery.data?.callsLastView),
    queryFn: async () => {
      const response = await API.get<MissedCallsCountResponse>(
        API_ENDPOINTS.CallLogsCount(personHistoryQuery.data!.callsLastView)
      );
      return response?.data;
    },
    enabled: !!personHistoryQuery.data?.callsLastView,
  });

  return {
    voicemailQuery,
    personHistoryQuery,
    missedCallsQuery,
  };
};

export const useResetMissedCalls = () => {
  return useMutation({
    mutationFn: async () => {
      const timestamp = getUnixTime(new Date());

      await API.post(API_ENDPOINTS.PersonHistory, {
        callsLastView: timestamp,
      });
      return timestamp;
    },
  });
};